import { useCallback, useEffect, useId, useState } from "react";

import { Button, Dialog, ErrorState, Field, Input, Notice, SkeletonRows, Status, Surface, Textarea, plainError } from "../../../../components/ui";
import { shortDate } from "../../../../components/calendar/calendarDays";
import { businessApi } from "../../../../services/api";

// How Apparify handles one employee's holiday pay (HC-D7): calculated with the standard rules, or checked by a person
// every time. It is a payroll handling choice recorded by HR with a start date and a reason — not a legal
// classification, and it never says anyone is covered or exempt. A record is never edited: a new one takes over from
// its start date and the earlier one stays in the history. The server decides who may record one.

export const HANDLING_OPTIONS = [
  {
    value: "STANDARD_RULES_APPLY",
    label: "Calculate with standard rules",
    summary: "Calculated with standard rules",
    help: "Use Apparify's supported nationwide holiday-pay rules automatically for this employee. Cases the system cannot determine still require review.",
  },
  {
    value: "REVIEW_REQUIRED",
    label: "Review holiday pay",
    summary: "Reviewed by HR each time",
    help: "Have HR check this employee's holiday pay whenever a holiday affects payroll. Use this when their situation or company policy needs a person to decide.",
  },
];
const optionOf = (value) => HANDLING_OPTIONS.find((option) => option.value === value) || null;
const REASON = { min: 10, max: 500 };

/** "1 Oct 2026" */
const day = (iso) => (iso ? shortDate(String(iso).slice(0, 10)) : "");
const recorded = (value) => (value ? new Date(value).toLocaleDateString("en-PH", { day: "numeric", month: "short", year: "numeric" }) : "");

const period = (fact) => (fact.effectiveUntil ? `${day(fact.effectiveFrom)} – ${day(fact.effectiveUntil)}` : `Since ${day(fact.effectiveFrom)}`);
const STATE_WORD = { upcoming: "Starts later", past: "Ended", cancelled: "Cancelled" };

function FactRow({ fact, muted = false }) {
  const option = optionOf(fact.handling);
  return (
    <div className="text-body-sm">
      <p className={muted ? "text-secondary" : "font-medium text-foreground"}>
        {option?.summary || "Holiday pay handling"}
        {fact.state !== "current" ? <Status className="ml-2 font-normal text-secondary">{STATE_WORD[fact.state] || ""}</Status> : null}
      </p>
      <p className="mt-0.5 text-meta text-secondary">{fact.state === "upcoming" ? `From ${day(fact.effectiveFrom)}` : period(fact)}</p>
      <p className="mt-1 text-foreground">Reason: {fact.reason}</p>
      <p className="mt-0.5 text-meta text-tertiary">
        Recorded{fact.recordedByName ? ` by ${fact.recordedByName}` : ""}{fact.recordedAt ? ` on ${recorded(fact.recordedAt)}` : ""}
      </p>
    </div>
  );
}

function HandlingDialog({ memberId, memberName, today, current, onClose, onSaved }) {
  const formId = useId();
  const [form, setForm] = useState({ handling: "", effectiveFrom: today || "", reason: "" });
  const [errors, setErrors] = useState({});
  const [failure, setFailure] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (key) => (event) => {
    setForm((value) => ({ ...value, [key]: event.target.value }));
    setErrors((value) => ({ ...value, [key]: undefined }));
    setFailure("");
  };

  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;
    const reason = form.reason.trim();
    const found = {};
    if (!optionOf(form.handling)) found.handling = "Choose how holiday pay is handled.";
    if (!form.effectiveFrom) found.effectiveFrom = "Choose the date this starts.";
    if (reason.length < REASON.min) found.reason = `Say why, in at least ${REASON.min} characters.`;
    else if (reason.length > REASON.max) found.reason = `Keep the reason to ${REASON.max} characters.`;
    setErrors(found);
    setFailure("");
    const first = ["handling", "effectiveFrom", "reason"].find((key) => found[key]);
    if (first) {
      document.getElementById(first === "handling" ? `${formId}-handling-${HANDLING_OPTIONS[0].value}` : `${formId}-${first}`)?.focus();
      return;
    }
    setSaving(true);
    try {
      const data = await businessApi.recordHolidayPayHandling(memberId, { handling: form.handling, effectiveFrom: form.effectiveFrom, reason });
      // The panel closes this dialog; nothing more is set here.
      onSaved(data, optionOf(form.handling));
    } catch (err) {
      setFailure(plainError(err, "This was not saved. Try again."));
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => { if (!open && !saving) onClose(); }}
      title={`Holiday pay handling for ${memberName}`}
      description="How Apparify treats this employee's holiday pay in payroll. It is not a legal classification."
      footer={(
        <>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" form={formId} variant="primary" loading={saving}>Save handling</Button>
        </>
      )}
    >
      <form id={formId} onSubmit={submit} noValidate className="space-y-4">
        <fieldset aria-describedby={errors.handling ? `${formId}-handling-error` : undefined}>
          <legend className="text-label text-secondary">Holiday pay <span aria-hidden="true" className="text-danger">*</span></legend>
          <div className="mt-1.5 divide-y divide-subtle rounded-control border border-default">
            {HANDLING_OPTIONS.map((option) => (
              <label key={option.value} htmlFor={`${formId}-handling-${option.value}`} className="flex cursor-pointer items-start gap-3 px-3 py-3 hover:bg-hover">
                <input
                  id={`${formId}-handling-${option.value}`}
                  type="radio"
                  name={`${formId}-handling`}
                  value={option.value}
                  checked={form.handling === option.value}
                  onChange={set("handling")}
                  disabled={saving}
                  required
                  className="mt-0.5 h-4 w-4 shrink-0"
                />
                <span className="min-w-0">
                  <span className="block text-body font-medium text-foreground">
                    {option.label}
                    {current === option.value ? <span className="font-normal text-secondary"> · current</span> : null}
                  </span>
                  <span className="mt-0.5 block text-body-sm text-secondary">{option.help}</span>
                </span>
              </label>
            ))}
          </div>
          {errors.handling ? <p id={`${formId}-handling-error`} role="alert" className="mt-1.5 text-meta text-danger">{errors.handling}</p> : null}
        </fieldset>
        <Field label="Starts on" required id={`${formId}-effectiveFrom`} error={errors.effectiveFrom}
          hint="Holidays on or after this date use this handling. It cannot reach into payroll already with Finance.">
          <Input type="date" value={form.effectiveFrom} onChange={set("effectiveFrom")} disabled={saving} />
        </Field>
        <Field label="Reason" required id={`${formId}-reason`} error={errors.reason} hint="Why this handling fits this employee. It is kept with the record.">
          <Textarea rows={3} maxLength={REASON.max} value={form.reason} onChange={set("reason")} disabled={saving} />
        </Field>
        {failure ? <Notice tone="danger" title="Not saved" role="alert">{failure}</Notice> : null}
      </form>
    </Dialog>
  );
}

export default function HolidayPayHandlingPanel({ memberId, memberName }) {
  const [state, setState] = useState({ loading: true, error: "", restricted: false, data: null });
  const [changing, setChanging] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: "" }));
    try {
      setState({ loading: false, error: "", restricted: false, data: await businessApi.getHolidayPayHandling(memberId) });
    } catch (err) {
      if (err?.status === 403) setState({ loading: false, error: "", restricted: true, data: null });
      else setState({ loading: false, error: plainError(err, "Check your connection and try again."), restricted: false, data: null });
    }
  }, [memberId]);

  useEffect(() => { load(); }, [load]);

  const { loading, error, restricted, data } = state;
  if (restricted) return null;

  const facts = data?.facts || [];
  const current = facts.find((fact) => fact.state === "current") || null;
  const upcoming = facts.filter((fact) => fact.state === "upcoming");
  const history = facts.filter((fact) => fact.state === "past" || fact.state === "cancelled");

  return (
    <Surface
      as="section"
      aria-label="Holiday pay handling"
      toolbar={(
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h2 className="text-panel-title text-foreground">Holiday pay</h2>
            <p className="max-w-[75ch] text-body-sm text-secondary">How payroll treats this employee's pay on official holidays. This is Apparify's handling, not a legal classification.</p>
          </div>
          {data?.canManage ? <Button size="sm" className="shrink-0" onClick={() => { setMessage(""); setChanging(true); }}>Change</Button> : null}
        </div>
      )}
    >
      {loading ? (
        <SkeletonRows rows={2} columns={2} label="Loading holiday pay handling" />
      ) : error ? (
        <ErrorState title="Holiday pay handling did not load" description={error} onRetry={load} />
      ) : (
        <div className="space-y-4 p-4">
          {current ? <FactRow fact={current} /> : (
            <div className="text-body-sm">
              <p className="font-medium text-foreground">Not set</p>
              <p className="mt-0.5 text-secondary">Until this is set, HR checks this employee's holiday pay each time a holiday affects payroll.</p>
            </div>
          )}
          {upcoming.map((fact) => <FactRow key={fact.id} fact={fact} />)}
          {history.length ? (
            <details>
              <summary className="cursor-pointer text-body-sm font-medium text-secondary hover:text-foreground">Earlier records ({history.length})</summary>
              <ul className="mt-2 space-y-3 border-l border-subtle pl-3">
                {history.map((fact) => <li key={fact.id}><FactRow fact={fact} muted /></li>)}
              </ul>
            </details>
          ) : null}
          <p role="status" aria-live="polite" className={message ? "text-body-sm text-secondary" : "sr-only"}>{message}</p>
        </div>
      )}
      {changing ? (
        <HandlingDialog
          memberId={memberId}
          memberName={memberName}
          today={data?.today}
          current={data?.current}
          onClose={() => setChanging(false)}
          onSaved={(next, option) => {
            setChanging(false);
            setState({ loading: false, error: "", restricted: false, data: next });
            setMessage(`Saved: ${option.summary}.`);
          }}
        />
      ) : null}
    </Surface>
  );
}
